import { CheckCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input } from 'antd';
import formatSeconds from 'commons/libs/duration_utils';
import { REQUEST_STATUSES } from 'commons/types/common';
import { useCallback, useState } from 'react';
import AuthCode from 'react-auth-code-input';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import {
  confirmMailOtpAsync,
  getConfirmOtpRequestStatus,
  getConflictError,
  getOtpRequestStatus,
  getThrottleError,
  requestMailOtpAsync,
} from 'stores/user/userSlice';

const VALIDATE_MESSAGES = {
  required: `ce champs est requis !`,
  types: {
    email: `email incorrect`,
  },
};

export default function UpdateEmail() {
  const dispatch = useAppDispatch();
  const mailOtpRequestStatus = useAppSelector(getOtpRequestStatus);
  const confirmMailOtpRequestStatus = useAppSelector(
    getConfirmOtpRequestStatus,
  );
  const throttleError = useAppSelector(getThrottleError);
  const conflictError = useAppSelector(getConflictError);
  const [step, setStep] = useState('email');
  const [newEmail, setNewEmail] = useState('');
  const onFinish = useCallback(
    ({ email }: { email: string }) => {
      dispatch(requestMailOtpAsync({ email }));
      setStep('code');
      setNewEmail(email);
    },
    [dispatch],
  );

  const resendCode = useCallback(() => {
    dispatch(requestMailOtpAsync({ email: newEmail }));
  }, [dispatch, newEmail]);

  const onFinishCode = useCallback(
    (otp: string) => {
      if (otp.length === 6) {
        dispatch(confirmMailOtpAsync({ otp, email: newEmail }));
      }
    },
    [dispatch, newEmail],
  );

  if (confirmMailOtpRequestStatus === REQUEST_STATUSES.SUCCESS) {
    return (
      <div className='flex justify-center text-center'>
        <div className='flex flex-col w-80 justify-center bg-white rounded-xl px-4 py-8 my-12'>
          <p className='font-bold text-xl'>Nouvel email confirmé</p>
          <CheckCircleOutlined className='text-7xl !text-[#12F16B] self-center' />
        </div>
      </div>
    );
  }

  return (
    <div className='flex text-center justify-center'>
      {step === 'code' && mailOtpRequestStatus === REQUEST_STATUSES.SUCCESS ? (
        <div className='flex flex-col w-80 justify-center bg-white rounded-xl px-4 py-8 my-12'>
          <p className='font-bold text-xl'>Code de vérification</p>
          <p className='text-xs mb-8'>
            Entrez le code que nous venons de vous envoyer au {newEmail}
          </p>
          <AuthCode
            containerClassName='flex flex-row'
            inputClassName='bg-white border-secondary w-1/2 rounded-xl mx-1 text-xl font-bold mb-4 text-center'
            allowedCharacters='numeric'
            onChange={onFinishCode}
          />
          {confirmMailOtpRequestStatus === REQUEST_STATUSES.ERROR &&
            !conflictError && (
              <p className='text-[10px] mt-2 text-red-600'>code invalide</p>
            )}
          {confirmMailOtpRequestStatus === REQUEST_STATUSES.ERROR &&
            conflictError && (
              <p className='text-[10px] mt-2 text-red-600'>
                Cet email est déjà attribué à un autre compte.
              </p>
            )}
          <p className='text-[10px] mt-2'>Le code est valide pendant 15 min</p>
          <Button
            type='link'
            className='text-primary mt-6'
            onClick={resendCode}
          >
            <p className='text-primary text-xs'>Renvoyer le code</p>
          </Button>
        </div>
      ) : (
        <div>
          <h2 className='text-lg font-semibold mb-8 pt-6'>
            Renseignez un nouvel email
          </h2>
          <Form
            className=''
            name='signin-magic'
            validateMessages={VALIDATE_MESSAGES}
            onFinish={onFinish}
          >
            <Form.Item
              name='email'
              rules={[{ required: true, type: 'email' }]}
            >
              <Input
                size='large'
                className='!rounded-[16px] h-10'
                defaultValue={newEmail}
                placeholder='e-mail'
              />
            </Form.Item>
            {mailOtpRequestStatus === REQUEST_STATUSES.ERROR && (
              <p className='text-[10px] !my-0 text-red-600'>
                L&apos;envoi a échoué.
              </p>
            )}
            {mailOtpRequestStatus === REQUEST_STATUSES.THROTTLED && (
              <p className='text-[10px] !my-0 text-red-600'>
                Veuillez patienter {formatSeconds(throttleError)} avant de
                réessayer.
              </p>
            )}
            <Form.Item>
              <Button
                type='primary'
                className='!rounded-[24px] !h-10 !font-semibold mt-8 w-full'
                loading={mailOtpRequestStatus === REQUEST_STATUSES.PENDING}
                htmlType='submit'
              >
                Recevoir l&apos;email
              </Button>
            </Form.Item>
          </Form>
        </div>
      )}
    </div>
  );
}
